import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send } from "lucide-react";

import api, { formatApiErrorDetail } from "@/lib/api";
import { ModuleTitleER } from "@/components/er/ModuleTitleER";
import { CATEGORY_LABELS, STATUS_LABELS, statusPillClass } from "./erLabels";

export default function ErMyReport() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/er/my-reports/${caseId}`);
      setCaseData(data.case);
      setMessages(data.messages || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      navigate("/er");
    }
  };

  useEffect(() => {
    load();
     
  }, [caseId]);

  const send = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/er/my-reports/${caseId}/messages`, { body: reply.trim() });
      setReply("");
      toast.success("Message sent.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!caseData) {
    return (
      <div className="patria-card p-8" data-testid="er-my-report-loading">
        <p className="text-charcoal-muted italic">Loading your report…</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="er-my-report-page">
      <Link to="/er" className="inline-flex items-center text-forest-700 text-sm font-medium">
        <ArrowLeft className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
        Back to ER Center
      </Link>

      <div>
        <ModuleTitleER />
        <div className="mt-3 flex flex-wrap items-baseline gap-3">
          <p className="text-xs uppercase tracking-widest text-charcoal-muted">
            {caseData.case_number}
          </p>
          <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs ${statusPillClass(caseData.status)}`}>
            {STATUS_LABELS[caseData.status] || caseData.status}
          </span>
        </div>
        <h1 className="font-serif text-forest-900 text-4xl mt-2">
          {CATEGORY_LABELS[caseData.category] || caseData.category}
        </h1>
        <p className="text-sm text-charcoal-muted mt-2">
          Submitted {new Date(caseData.created_at).toLocaleString()}
        </p>
      </div>

      <section className="patria-card p-6">
        <h3 className="font-serif text-forest-900 text-xl">Your Report</h3>
        <p className="mt-3 text-charcoal whitespace-pre-line">{caseData.description}</p>
        {caseData.parties_involved && (
          <>
            <p className="mt-4 text-xs uppercase tracking-widest text-charcoal-muted">
              Parties involved
            </p>
            <p className="mt-1 text-charcoal-soft">{caseData.parties_involved}</p>
          </>
        )}
      </section>

      <section className="patria-card p-6">
        <h3 className="font-serif text-forest-900 text-xl">Messages With People Operations</h3>
        <ul className="mt-4 space-y-3">
          {messages.length === 0 && (
            <li className="text-sm text-charcoal-muted italic">
              No messages yet. When People Operations replies, it will appear here.
            </li>
          )}
          {messages.map((m) => (
            <li
              key={m.id}
              className={`patria-card p-4 ${m.sender_type === "po" ? "border-l-4 border-terracotta-500" : "border-l-4 border-forest-600"}`}
              data-testid={`er-my-report-msg-${m.id}`}
            >
              <p className="text-xs uppercase tracking-widest text-charcoal-muted">
                {m.sender_type === "po" ? `${m.sender_name} • People Operations` : "You"} —{" "}
                {new Date(m.created_at).toLocaleString()}
              </p>
              <p className="mt-2 text-charcoal whitespace-pre-line">{m.body}</p>
            </li>
          ))}
        </ul>

        <div className="mt-5">
          <label className="patria-label">Send a message</label>
          <textarea
            className="patria-input mt-2"
            rows={4}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder="Add context, ask a question, or thank People Operations."
            data-testid="er-my-report-reply"
          />
          <div className="mt-3 flex justify-end">
            <button
              onClick={send}
              disabled={!reply.trim() || busy}
              className="patria-btn-primary"
              data-testid="er-my-report-send"
            >
              <Send className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
              Send message
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
